import React, {useEffect, useState} from 'react'
import {Typography} from '@mui/material'
import {pluralizeWord} from '../../Utils/StringFormatter'
import {rxrPrimaryColor, rxrRedColor, rxrContainerColor, rxrGreyColor, rxrSuccessColor} from '../../assets/styles/color'
import {spaceExtraExtraSmall, spaceMedium, spaceExtraLarge, spaceSmall} from '../../assets/styles/spacing'
import {getPaymentById} from '../../lib/queries'
import moment from 'moment/moment'
import {useDispatch, useSelector} from 'react-redux'
import {makeStyles} from '@mui/styles'
import Constant from './AmenitiesConstants'
import PropTypes from 'prop-types'
import RXRButton from '../RXRButton'
import RXRIcon from '../RXRIcon'
import NumberInput from '../NumberInput'
import {
  updateAmenityReservationPaymentSettings,
  chargeForAmenityReservation,
  refundAmenityReservationDeposit,
  markAmenityReservationAsPaidInPerson,
} from '../../actions/amenitiesActions'
import {fontSizeSmall} from '../../assets/styles/typography'
import ConfirmDeleteDialog from '../ConfirmDeleteDialog'
const {PAYMENT_STATE_PAID, PAYMENT_STATE_PROCESSING, PAYMENT_STATE_DECLINED, PAYMENT_STATE_CANCELLED, PAYMENT_STATE_REFUNDED} = Constant

const useStyles = makeStyles({
  priceBreakdownTitle: {
    color: rxrPrimaryColor,
    marginBottom: spaceSmall,
  },
  priceBreakdownContainer: {
    backgroundColor: rxrContainerColor,
    padding: spaceSmall,
    border: `1px solid ${rxrGreyColor}`,
    borderRadius: spaceExtraExtraSmall,
  },
  priceBreakdownItem: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  priceBreakdownDivider: {
    border: 0,
    borderTop: `1.5px solid ${rxrGreyColor}`,
    margin: `${spaceSmall}px 0`,
  },
  priceBreakdownTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    color: rxrPrimaryColor,
  },
  priceBreakdownDeposit: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: spaceSmall,
    color: rxrPrimaryColor,
  },
  boldText: {
    fontWeight: 'bold',
  },
  totalLabel: {
    fontWeight: 'bold',
  },

  section: {
    marginBottom: spaceExtraLarge,
  },

  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: spaceMedium,
    gap: spaceMedium,

    '& > *': {
      flex: 2,

      '&:nth-child(3)': {
        flex: 1,
      },
    },
  },

  instructions: {
    margin: `${spaceSmall}px 0`,
    fontStyle: 'italic',
    fontSize: fontSizeSmall,
  },

  instructionsRed: {
    margin: `${spaceSmall}px 0`,
    color: rxrRedColor,
  },
})

function BookingPriceBreakdown(props) {
  const classes = useStyles()
  const isEditMode = !!props.id

  const currentLoggedInStaffId = useSelector(state => state.Staff.authedStaffModel.id)
  const dispatch = useDispatch()
  const amenitiesLookup = useSelector(state => state.Amenities.amenitiesLookup)
  const amenity = props.amenityId ? amenitiesLookup[props.amenityId] : null
  const amenityReservationsLookup = useSelector(state => state.Amenities.amenityReservationsLookup)
  const existingReservation = isEditMode ? amenityReservationsLookup[props.id] : null

  const durationInHours = props.startAt && props.endAt ? moment(props.endAt).diff(moment(props.startAt), 'minutes') / 60 : 0
  const hourlyPrice = isEditMode ? props.hourlyCost || 0 : (amenity && amenity.hourlyPrice) || 0
  const depositCost = isEditMode ? props.depositCost || 0 : (amenity && amenity.depositPrice) || 0

  const [depositPayment, setDepositPayment] = useState(null)
  const [bookingPayment, setBookingPayment] = useState(null)
  const [isLoadingPayments, setIsLoadingPayments] = useState(false)
  const [isSavingChanges, setIsSavingChanges] = useState(false)
  const [refundError, setRefundError] = useState(null)
  const [chargeError, setChargeError] = useState(null)
  const [amountToRefund, setAmountToRefund] = useState(depositCost)
  const [hoursBooked, setHoursBooked] = useState(durationInHours)
  const [showChargeSuccess, setShowChargeSuccess] = useState(false)
  const [confirmPaidInPerson, setConfirmPaidInPerson] = useState(false)

  const bookingCost = (hoursBooked || durationInHours) * hourlyPrice

  useEffect(() => {
    const queries = []
    if (props.depositPaymentId) {
      queries.push(getPaymentById(props.depositPaymentId))
    } else {
      queries.push(Promise.resolve(null)) // No deposit payment ID, resolve with null
    }

    if (props.bookingPaymentId) {
      queries.push(getPaymentById(props.bookingPaymentId))
    } else {
      queries.push(Promise.resolve(null)) // No booking payment ID, resolve with null
    }

    setIsLoadingPayments(true)
    Promise.all(queries)
      .then(([d, b]) => {
        setDepositPayment(d)
        setBookingPayment(b)
      })
      .catch(err => {
        console.error('Error fetching payments', err)
        setDepositPayment(null) // or handle error state
      })
      .finally(() => {
        setIsLoadingPayments(false)
      })
  }, [props.depositPaymentId, props.bookingPaymentId])

  const getPaymentStatusText = p => {
    if (isLoadingPayments) {
      return 'loading...'
    }
    if (!p) {
      return 'not yet paid'
    }

    switch (p.status) {
      case PAYMENT_STATE_PAID:
        return 'paid'
      case PAYMENT_STATE_PROCESSING:
        return 'processing'
      case PAYMENT_STATE_DECLINED:
        return 'declined'
      case PAYMENT_STATE_CANCELLED:
        return 'cancelled'
      case PAYMENT_STATE_REFUNDED:
        return 'refunded'
      default:
        return 'unknown status'
    }
  }

  const handleChargeBookingFee = () => {
    setIsSavingChanges(true)
    setChargeError(null)
    chargeForAmenityReservation(dispatch, props.id)
      .then(payment => {
        // Use the payment returned directly from the charge action (if it exists)
        if (payment) {
          setBookingPayment(payment)
        }

        // Show success state regardless of payment object (for development compatibility)
        setShowChargeSuccess(true)
      })
      .catch(err => {
        console.error('Error charging for amenity reservation', err)
        setChargeError(err.message)
      })
      .finally(() => {
        setIsSavingChanges(false)
      })
  }

  const handleMarkPaidInPerson = () => {
    if (!confirmPaidInPerson) {
      setConfirmPaidInPerson(true)
      return
    }

    setConfirmPaidInPerson(false)
    setIsSavingChanges(true)
    setChargeError(null)
    markAmenityReservationAsPaidInPerson(dispatch, existingReservation, bookingCost, currentLoggedInStaffId)
      .then(() => {
        // Show success state
        setShowChargeSuccess(true)
      })
      .catch(err => {
        console.error('Error marking amenity reservation as paid in person', err)
        setChargeError(err.message)
      })
      .finally(() => {
        setIsSavingChanges(false)
      })
  }

  const handleRefundDeposit = () => {
    if (amountToRefund <= 0) {
      console.warn('Refund amount must be greater than or equal zero')
      return
    }

    setRefundError(null)
    setIsSavingChanges(true)
    refundAmenityReservationDeposit(
      dispatch,
      props.residentId,
      props.id,
      props.depositPaymentId,
      amountToRefund,
      depositCost - amountToRefund,
    )
      .then(() => {
        return getPaymentById(props.depositPaymentId).then(payment => {
          setDepositPayment(payment)
        })
      })
      .catch(err => {
        console.error('Error refunding deposit', err)
        setRefundError(err.message)
      })
      .finally(() => {
        setIsSavingChanges(false)
      })
  }

  const handleChangeHours = () => {
    if (typeof hoursBooked !== 'number' || isNaN(hoursBooked) || hoursBooked < 0 || hoursBooked === durationInHours) {
      // no change
      return
    }

    setIsSavingChanges(true)
    updateAmenityReservationPaymentSettings(dispatch, {
      id: props.id,
      startAt: props.startAt,
      endAt: moment(props.startAt)
        .add(hoursBooked * 60, 'minutes')
        .toDate(),
    })
      .catch(err => {
        console.error('Error updating amenity reservation payment settings', err)
      })
      .finally(() => {
        setIsSavingChanges(false)
      })
  }

  const depositIsOutstanding = depositCost > 0 && (!depositPayment || depositPayment.status !== PAYMENT_STATE_REFUNDED)

  const isBookingPaymentCTADisabled =
    // if the payment exists and is not cancelled or declined then you cannot charge again
    (bookingPayment && ![PAYMENT_STATE_DECLINED, PAYMENT_STATE_CANCELLED].includes(bookingPayment.status)) ||
    // or the hours have been modified (and not saved yet)
    hoursBooked !== durationInHours ||
    // or they clicked charge and it was successful
    showChargeSuccess

  return (
    <React.Fragment>
      {depositCost > 0 && (
        <div className={classes.section}>
          <Typography className={classes.priceBreakdownTitle}>Deposit</Typography>
          {depositPayment && props.readyForPayment && (
            <Typography className={classes.instructions}>
              Refunds will take 3-5 business days to complete. Refund is only available within 90 days of the payment date.
            </Typography>
          )}
          <div className={classes.priceBreakdownContainer}>
            <div className={classes.priceBreakdownTotal}>
              <Typography className={classes.boldText}>Deposit</Typography>
              <Typography className={classes.boldText}>{`$${depositCost.toFixed(2)}`}</Typography>
            </div>
            <hr className={classes.priceBreakdownDivider} />
            {depositPayment && (
              <div className={classes.priceBreakdownDeposit}>
                <Typography>Payment - {getPaymentStatusText(depositPayment)}</Typography>
                <Typography>
                  {
                    depositPayment.status === PAYMENT_STATE_REFUNDED
                      ? `($${depositPayment.unitAmount.toFixed(2)})`
                      : `$${depositPayment.unitAmount.toFixed(2)}`
                  }
                </Typography>
              </div>
            )}
          </div>
          {depositPayment && props.readyForPayment && (
            <React.Fragment>
              <div className={classes.buttonsContainer}>
                <NumberInput
                  label="Amount to refund"
                  prefix="$"
                  isDisabled={depositPayment.status !== PAYMENT_STATE_PAID}
                  value={amountToRefund}
                  onChange={setAmountToRefund}
                />
                <RXRButton
                  onClick={handleRefundDeposit}
                  disabled={depositPayment.status !== PAYMENT_STATE_PAID}
                  isLoading={props.ctasDisabled || isSavingChanges || isLoadingPayments}
                >
                  Refund deposit
                </RXRButton>
              </div>
              {refundError && <Typography className={classes.instructionsRed}>{refundError}</Typography>}
            </React.Fragment>
          )}
        </div>
      )}

      <div className={classes.section}>
        <Typography className={classes.priceBreakdownTitle}>Price Breakdown</Typography>
        {depositIsOutstanding && (
          <Typography className={classes.instructionsRed}>
            You will not be able to refund the deposit once the booking is charged. Please refund the deposit if desired before proceeding.
          </Typography>
        )}
        <div className={classes.priceBreakdownContainer}>
          <div className={classes.priceBreakdownItem}>
            <Typography>{pluralizeWord('Hour', hoursBooked || durationInHours, true)}</Typography>
            <Typography>{`$${bookingCost.toFixed(2)}`}</Typography>
          </div>
          <div className={classes.priceBreakdownItem}>
            <Typography>Tax (not collected)</Typography>
            <Typography>$0</Typography>
          </div>
          <hr className={classes.priceBreakdownDivider} />
          <div className={classes.priceBreakdownTotal}>
            <Typography className={classes.boldText}>Total</Typography>
            <Typography className={classes.boldText}>{`$${bookingCost.toFixed(2)}`}</Typography>
          </div>
          {bookingPayment ? (
            <div className={classes.priceBreakdownDeposit}>
              <Typography>Payment - ({getPaymentStatusText(bookingPayment)})</Typography>
              <Typography>{`$${bookingPayment.unitAmount.toFixed(2)}`}</Typography>
            </div>
          ) : null}
        </div>
        {props.readyForPayment && (
          <React.Fragment>
            <div className={classes.buttonsContainer}>
              <NumberInput
                label="Hours"
                value={hoursBooked}
                onChange={setHoursBooked}
                min={0}
                onBlur={handleChangeHours}
                helperText={hoursBooked !== durationInHours ? 'Click out to save your changes' : ''}
              />
              <RXRButton
                onClick={handleChargeBookingFee}
                isLoading={props.ctasDisabled || isSavingChanges || isLoadingPayments}
                disabled={isBookingPaymentCTADisabled}
              >
                {showChargeSuccess ? (
                  <React.Fragment>
                    <RXRIcon icon={RXRIcon.CHECK} color={rxrSuccessColor} />
                    &nbsp;Payment Successful
                  </React.Fragment>
                ) : (
                  'Charge for Booking'
                )}
              </RXRButton>

              {!showChargeSuccess && (
                <RXRButton
                  onClick={handleMarkPaidInPerson}
                  isLoading={props.ctasDisabled || isSavingChanges || isLoadingPayments}
                  disabled={isBookingPaymentCTADisabled}
                  type={RXRButton.TYPE_SECONDARY}
                >
                  Paid in Person
                </RXRButton>
              )}
            </div>
            {chargeError && <Typography className={classes.instructionsRed}>{chargeError}</Typography>}
          </React.Fragment>
        )}
      </div>

      <ConfirmDeleteDialog
        isOpen={confirmPaidInPerson}
        prompt={
          'Are you sure you want to mark this booking as paid in person? You will not be able to issue a charge to the associated payment method after doing this.'
        }
        onConfirmDelete={handleMarkPaidInPerson}
        onCancel={() => setConfirmPaidInPerson(false)}
        deleteButtonText={'Confirm paid in person'}
      />
    </React.Fragment>
  )
}

BookingPriceBreakdown.propTypes = {
  amenityId: PropTypes.string,
  residentId: PropTypes.string,
  id: PropTypes.string,
  startAt: PropTypes.any,
  endAt: PropTypes.any,
  hourlyCost: PropTypes.number,
  depositCost: PropTypes.number,
  depositPaymentId: PropTypes.string,
  bookingPaymentId: PropTypes.string,
  readyForPayment: PropTypes.bool,
  ctasDisabled: PropTypes.bool,
}

export default BookingPriceBreakdown
